import { useState, useEffect, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { ChecklistCategory } from "@/lib/appointment-checklist";
import { AppointmentTask } from "@/models/appointment-task.model";
import { CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getChecklistProgress, buildLatestTaskMap } from "@/lib/utils/checklist-progress";

interface TaskChecklistProps {
  serviceTasks: ChecklistCategory[];
  appointmentTasks: AppointmentTask[];
  onTaskToggle: (taskId: string, done: boolean) => void;
  readOnly?: boolean;
  // disabled: an earlier flow step is unmet (not clocked in, before photos
  // missing) — disabledMessage names that step.
  disabled?: boolean;
  disabledMessage?: string | null;
}

export function TaskChecklist({
  serviceTasks,
  appointmentTasks,
  onTaskToggle,
  readOnly = false,
  disabled = false,
  disabledMessage,
}: TaskChecklistProps) {
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const categoryIds = serviceTasks.map((category) => category.id).join(",");

  useEffect(() => {
    if (categoryIds !== "") setExpandedCategories(new Set(categoryIds.split(",")));
  }, [categoryIds]);

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const latestTaskMap = useMemo(() => buildLatestTaskMap(appointmentTasks), [appointmentTasks]);
  const isTaskCompleted = (taskId: string) => !!latestTaskMap.get(taskId)?.done;

  const getCategoryProgress = (category: ChecklistCategory) => {
    if (!category.tasks || category.tasks.length === 0) return { completed: 0, total: 0 };

    const completed = category.tasks.filter((task) => isTaskCompleted(task.id)).length;
    const total = category.tasks.length;

    return { completed, total };
  };

  const overallProgress = useMemo(
    () => getChecklistProgress(serviceTasks, appointmentTasks),
    [serviceTasks, appointmentTasks]
  );

  if (serviceTasks.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>No tasks defined for this service.</p>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", disabled && "opacity-60 pointer-events-none")}>
      {disabled && (
        <div className="bg-muted rounded-lg p-3 text-sm text-muted-foreground text-center">
          {disabledMessage ?? "Start work to begin checking tasks"}
        </div>
      )}
      {/* Overall Progress */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Overall Progress</h3>
          <Badge variant="secondary">
            {overallProgress.completed} of {overallProgress.total} tasks completed
          </Badge>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width: overallProgress.total > 0 ? `${(overallProgress.completed / overallProgress.total) * 100}%` : "0%",
            }}
          />
        </div>
      </div>

      {/* Task Categories */}
      <div className="space-y-3">
        {serviceTasks.map((category) => {
          const isExpanded = expandedCategories.has(category.id);
          const categoryProgress = getCategoryProgress(category);

          return (
            <Card key={category.id} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors select-none"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-primary transition-transform" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform" />
                    )}
                    <span className="font-medium">{category.title}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      ({categoryProgress.total} task{categoryProgress.total !== 1 ? "s" : ""})
                    </span>
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {categoryProgress.completed}/{categoryProgress.total}
                    </Badge>
                    <Badge
                      variant={categoryProgress.completed === categoryProgress.total ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {categoryProgress.total > 0
                        ? Math.round((categoryProgress.completed / categoryProgress.total) * 100)
                        : 0}
                      %
                    </Badge>
                  </div>
                </div>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {category.tasks && category.tasks.length > 0 ? (
                      category.tasks.map((task) => {
                        const isCompleted = isTaskCompleted(task.id);

                        return (
                          <div
                            key={task.id}
                            className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/30 transition-colors bg-background"
                          >
                            <Checkbox
                              id={task.id}
                              checked={isCompleted}
                              onCheckedChange={
                                readOnly || disabled ? undefined : (checked) => onTaskToggle(task.id, checked as boolean)
                              }
                              disabled={readOnly || disabled}
                              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <label
                              htmlFor={task.id}
                              className="flex-1 cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 hover:text-primary transition-colors"
                            >
                              {task.title}
                            </label>
                            {isCompleted && <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No tasks in this category</p>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
